<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.battles', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                    <div :class="terrain ? 'visible' : 'invisible'" class="h-12">
                        <img
                            :src="terrain.image_path"
                            class="h-full"
                            :alt="$tc('messages.battle_terrains', 1)"
                            :title="terrain.text"
                            v-if="terrain"
                        />
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.name')" for="name" />
                            <FormInput
                                id="name"
                                v-model="form.name"
                                name="name"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.group')" for="group_id" />
                            <FormSelect
                                required
                                id="group_id"
                                v-model="form.group_id"
                                :options="options.groups"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.group_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.battle_terrains', 1)" for="battle_terrain_id" />
                            <BattleTerrainsSelectorPanel
                                :terrains="options.battle_terrains"
                                v-model="form.battle_terrain_id"
                                :endpoint="meta.links.terrain_store"
                            />
                            <FormError :value="form.errors.battle_terrain_id" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.battle_enemies', 1) }}
                    </div>
                    <div :class="enemy ? 'visible' : 'invisible'" class="h-12">
                        <img
                            :src="enemy.image_path"
                            class="h-full"
                            :alt="$tc('messages.battle_enemies', 1)"
                            :title="enemy.text"
                            v-if="enemy"
                        />
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.battle_enemies', 1)" for="battle_enemy_id" />
                            <BattleEnemiesSelectorPanel
                                :enemies="options.battle_enemies"
                                v-model="form.battle_enemy_id"
                                :endpoint="meta.links.enemy_store"
                            />
                            <FormError :value="form.errors.battle_enemy_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.enemy_health_total')" for="enemy_health_total" />
                            <FormInput
                                required
                                id="enemy_health_total"
                                v-model.number="form.enemy_health_total"
                                name="enemy_health_total"
                                type="number"
                                min="1"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.enemy_health_total" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.enemy_damage')" for="enemy_damage" />
                            <FormInput
                                id="enemy_damage"
                                v-model.number="form.enemy_damage"
                                name="enemy_damage"
                                type="number"
                                min="0"
                                :placeholder="$t('messages.(optional)')"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.enemy_damage" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.gamification_rewards', 2) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.coins_amount')" for="coins_amount" />
                            <FormInput
                                id="coins_amount"
                                v-model.number="form.coins_amount"
                                name="coins_amount"
                                type="text"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.coins_amount" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.settings') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_reward_shown')" for="is_reward_shown" />
                            <input
                                id="is_reward_shown"
                                type="checkbox"
                                v-model="form.is_reward_shown"
                                name="is_reward_shown"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_reward_shown" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_battle_automatically_ended')"
                                for="is_automatically_ended"
                            />
                            <input
                                id="is_automatically_ended"
                                type="checkbox"
                                v-model="form.is_automatically_ended"
                                name="is_automatically_ended"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_automatically_ended" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_enemy_health_shown')" for="is_enemy_health_shown" />
                            <input
                                id="is_enemy_health_shown"
                                type="checkbox"
                                v-model="form.is_enemy_health_shown"
                                name="is_enemy_health_shown"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_enemy_health_shown" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.max_actions_per_student')" for="max_actions_per_student" />
                            <FormInput
                                id="max_actions_per_student"
                                v-model.number="form.max_actions_per_student"
                                name="max_actions_per_student"
                                type="number"
                                min="0"
                                :placeholder="$t('messages.(optional)')"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.max_actions_per_student" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.battle_reward_mode')" for="is_reward_evenly_split" />
                            <FormRadioGroupCards
                                name="is_reward_evenly_split"
                                v-model="form.is_reward_evenly_split"
                                :options="options.victoryRewardOptions"
                            />
                            <FormError :value="form.errors.is_reward_shown" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.date') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.started_at')" for="started_at" />
                            <FormInput
                                id="started_at"
                                v-model="form.started_at"
                                name="started_at"
                                type="datetime-local"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.started_at" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.finished_at')" for="finished_at" />
                            <FormInput
                                id="finished_at"
                                v-model="form.finished_at"
                                name="finished_at"
                                type="datetime-local"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.finished_at" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.description') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.victory_text')" for="victory_text" />
                            <FormTextarea
                                id="victory_text"
                                v-model="form.victory_text"
                                name="victory_text"
                                class="mt-1"
                                :placeholder="$t('messages.(optional)')"
                            />
                            <FormError :value="form.errors.victory_text" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.defeat_text')" for="defeat_text" />
                            <FormTextarea
                                id="defeat_text"
                                v-model="form.defeat_text"
                                name="defeat_text"
                                class="mt-1"
                                :placeholder="$t('messages.(optional)')"
                            />
                            <FormError :value="form.errors.defeat_text" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import AppButton from '@/components/AppButton.vue';
import BattleEnemiesSelectorPanel from '@/components/BattleEnemiesSelectorPanel.vue';
import BattleTerrainsSelectorPanel from '@/components/BattleTerrainsSelectorPanel.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import FormRadioGroupCards from '@/components/Forms/FormRadioGroupCards.vue';

export default {
    components: {
        FormRadioGroupCards,
        FormTextarea,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        AppButton,
        BattleEnemiesSelectorPanel,
        BattleTerrainsSelectorPanel,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'options', 'battle', 'gamificationSet'],
    data() {
        return {
            form: this.$inertia.form({
                name: this.battle.name,
                battle_enemy_id: this.battle.battle_enemy_id,
                battle_terrain_id: this.battle.battle_terrain_id,
                group_id: this.battle.group_id,
                enemy_health_total: this.battle.enemy_health_total,
                enemy_damage: this.battle.enemy_damage,
                coins_amount: this.battle.coins_amount,
                is_reward_evenly_split: this.battle.is_reward_evenly_split,
                is_reward_shown: this.battle.is_reward_shown,
                is_enemy_health_shown: this.battle.is_enemy_health_shown,
                max_actions_per_student: this.battle.max_actions_per_student,
                is_automatically_ended: this.battle.is_automatically_ended,
                started_at: this.battle.started_at,
                finished_at: this.battle.finished_at,
                victory_text: this.battle.victory_text,
                defeat_text: this.battle.defeat_text,
            }),
        };
    },
    computed: {
        enemy() {
            const enemy = this.options.battle_enemies.find((e) => e.value == this.form.battle_enemy_id);

            return enemy ? enemy : null;
        },
        terrain() {
            const terrain = this.options.battle_terrains.find((e) => e.value == this.form.battle_terrain_id);

            return terrain ? terrain : null;
        },
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
    },
};
</script>
